import React, { useState, useEffect } from 'react';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { Dish, Pack } from '../../types';
import DishFormModal from '../../components/DishFormModal';
import ExtraFormModal from '../../components/ExtraFormModal';
import PackFormModal from '../../components/PackFormModal';
import { PlusIcon, PencilIcon, TrashIcon, FireIcon, XIcon, SearchIcon } from '../../components/icons';
import { motion, AnimatePresence } from 'framer-motion';

const ChevronIcon = ({ className, isOpen }: { className?: string, isOpen: boolean }) => (
    <svg className={`${className} transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
    </svg>
);

const AdminDishesScreen: React.FC = () => {
    const { restaurants, loading, addDish, updateDish, deleteDish, addExtra, updateExtra, deleteExtra, addPack, updatePack, deletePack, ensureDishesLoaded, ensurePacksLoaded } = useData();
    const { currentUser } = useAuth();
    
    // Correction: Derivation du restaurant géré localement
    const managedRestaurant = React.useMemo(() => 
        restaurants.find(r => r.ownerId === currentUser?.uid || (r as any).owner_id === currentUser?.uid),
    [restaurants, currentUser]);
    
    const [viewMode, setViewMode] = useState<'dishes' | 'extras' | 'packs'>('dishes');
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isExtraModalOpen, setIsExtraModalOpen] = useState(false);
    const [isPackModalOpen, setIsPackModalOpen] = useState(false);
    const [isPageJustLoaded, setIsPageJustLoaded] = useState(true);
    
    const [dishToEdit, setDishToEdit] = useState<Dish | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string>('Burgers');
    const [extraToEdit, setExtraToEdit] = useState<any>(null);
    const [packToEdit, setPackToEdit] = useState<Pack | null>(null);
    
    const [isSaving, setIsSaving] = useState(false);
    const [isExtrasSaving, setIsExtrasSaving] = useState(false);
    const [isPacksSaving, setIsPacksSaving] = useState(false);

    // Accordéon : gestion des catégories dépliées
    const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

    const toggleCategory = (catName: string) => {
        setExpandedCategories(prev => ({ ...prev, [catName]: !prev[catName] }));
    };

    const jumpToCategory = (catName: string) => {
        setExpandedCategories(prev => ({ ...prev, [catName]: true }));
        setTimeout(() => {
            document.getElementById(`cat-${catName}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    const handleManualSync = async () => {
        if (managedRestaurant?.id) {
            console.log("🔄 Manual Sync triggered for:", managedRestaurant.id);
            await ensureDishesLoaded(managedRestaurant.id);
            await ensurePacksLoaded(managedRestaurant.id);
            alert("Synchronisation réussie !");
        }
    };

    useEffect(() => {
        // SAFETY RESET: Ensure no modal is open when entering this screen
        setIsFormModalOpen(false);
        setIsExtraModalOpen(false);
        setIsPackModalOpen(false);
        
        // Prevent accidental clicks on mobile during transition
        const timer = setTimeout(() => setIsPageJustLoaded(false), 500);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (managedRestaurant?.id) {
            ensureDishesLoaded(managedRestaurant.id);
            ensurePacksLoaded(managedRestaurant.id);
            
            if (managedRestaurant.menu) {
                const allExpanded: Record<string, boolean> = {};
                managedRestaurant.menu.forEach(cat => {
                    allExpanded[cat.category] = true;
                });
                setExpandedCategories(allExpanded);
            }
        }
    }, [managedRestaurant?.id, managedRestaurant?.menu?.length]);

    const handleOpenAddModal = (category: string) => {
        setDishToEdit(null);
        setSelectedCategory(category);
        setIsFormModalOpen(true);
    };

    const handleOpenEditModal = (dish: Dish, category: string) => {
        setDishToEdit(dish);
        setSelectedCategory(category);
        setIsFormModalOpen(true);
    };

    const handleCloseFormModal = () => {
        setIsFormModalOpen(false);
        setDishToEdit(null);
    };

    const handleSaveDish = async (dishData: any) => {
        if (!managedRestaurant) return;
        setIsSaving(true);
        try {
            if (dishToEdit) {
                await updateDish(managedRestaurant.id, { ...dishData, id: dishToEdit.id });
            } else {
                await addDish(managedRestaurant.id, dishData);
            }
            handleCloseFormModal();
        } catch (error) {
            console.error("Error saving dish:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteDish = async (dishId: string) => {
        if (!managedRestaurant || !window.confirm("Supprimer ce plat ?")) return;
        await deleteDish(managedRestaurant.id, dishId);
    };

    const handleOpenAddExtraModal = () => {
        setExtraToEdit(null);
        setIsExtraModalOpen(true);
    };

    const handleOpenEditExtraModal = (extra: any) => {
        setExtraToEdit(extra);
        setIsExtraModalOpen(true);
    };

    const handleCloseExtraModal = () => {
        setIsExtraModalOpen(false);
        setExtraToEdit(null);
    };

    const handleSaveExtra = async (extraData: any) => {
        if (!managedRestaurant) return;
        setIsExtrasSaving(true);
        try {
            if (extraToEdit) {
                await updateExtra(managedRestaurant.id, extraToEdit.id, extraData);
            } else {
                await addExtra(managedRestaurant.id, extraData);
            }
            handleCloseExtraModal();
        } catch (error) {
            console.error("Error saving extra:", error);
        } finally {
            setIsExtrasSaving(false);
        }
    };

    const handleDeleteGlobalExtra = async (extraId: string) => {
        if (!managedRestaurant || !window.confirm("Supprimer cet extra ?")) return;
        await deleteExtra(managedRestaurant.id, extraId);
    };

    const handleOpenAddPackModal = () => {
        setPackToEdit(null);
        setIsPackModalOpen(true);
    };

    const handleOpenEditPackModal = (pack: Pack) => {
        setPackToEdit(pack);
        setIsPackModalOpen(true);
    };

    const handleClosePackModal = () => {
        setIsPackModalOpen(false);
        setPackToEdit(null);
    };

    const handleSavePack = async (packData: any) => {
        if (!managedRestaurant) return;
        setIsPacksSaving(true);
        try {
            if (packToEdit) {
                await updatePack(managedRestaurant.id, { ...packData, id: packToEdit.id });
            } else {
                await addPack(managedRestaurant.id, packData);
            }
            handleClosePackModal();
        } catch (error) {
            console.error("Error saving pack:", error);
        } finally {
            setIsPacksSaving(false);
        }
    };

    const handleDeletePack = async (packId: string) => {
        if (!managedRestaurant || !window.confirm("Supprimer ce pack ?")) return;
        await deletePack(managedRestaurant.id, packId);
    };

    if (loading && !managedRestaurant) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div></div>;

    if (!managedRestaurant) return <div className="p-8 text-center">Restaurant introuvable.</div>;

    return (
        <div className="bg-gray-50 min-h-screen pb-32 overflow-y-auto">
            <div className="p-4 max-w-7xl mx-auto space-y-6">
                <div className="sticky top-0 z-30 flex flex-col items-center bg-white p-4 md:p-6 mb-2 rounded-b-3xl shadow-xl border-b border-gray-100 -mx-4 md:mx-0">
                    <div className="flex justify-between items-center w-full mb-4">
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-black text-gray-900 uppercase italic tracking-tighter">Gestion Menu</h1>
                            <button onClick={handleManualSync} className="p-2 bg-gray-50 rounded-full text-blue-600 active:rotate-180 transition-transform"><FireIcon className="w-5 h-5" /></button>
                        </div>
                    </div>
                    
                    <div className="flex w-full bg-gray-100 p-1 rounded-2xl gap-1">
                        <button 
                            onClick={() => setViewMode('dishes')} 
                            className={`flex-1 py-3 rounded-xl text-xs font-black uppercase transition-all shadow-sm ${viewMode === 'dishes' ? 'bg-red-600 text-white scale-105' : 'text-gray-500'}`}
                        >
                            Plats
                        </button>
                        <button 
                            onClick={() => setViewMode('extras')} 
                            className={`flex-1 py-3 rounded-xl text-xs font-black uppercase transition-all shadow-sm ${viewMode === 'extras' ? 'bg-green-600 text-white scale-105' : 'text-gray-500'}`}
                        >
                            Extras
                        </button>
                        <button 
                            onClick={() => setViewMode('packs')} 
                            className={`flex-1 py-3 rounded-xl text-xs font-black uppercase transition-all shadow-sm ${viewMode === 'packs' ? 'bg-blue-600 text-white scale-105' : 'text-gray-500'}`}
                        >
                            Packs
                        </button>
                    </div>
                </div>

                <div className="bg-white p-4 md:p-8 rounded-2xl shadow-sm border border-gray-100 min-h-[500px]">
                    <div className="mb-6">
                        <h2 className="text-2xl font-black tracking-tighter uppercase italic border-l-8 border-current pl-4" style={{ color: viewMode === 'dishes' ? '#dc2626' : viewMode === 'extras' ? '#16a34a' : '#2563eb' }}>
                            {viewMode === 'dishes' ? 'Modifier les plats' : viewMode === 'extras' ? 'Modifier les extras' : 'Modifier les packs'}
                        </h2>
                    </div>


                    {viewMode === 'dishes' && (
                        <div className="space-y-6">
                            {/* Barre de navigation par catégorie */}
                            <div className="sticky top-[130px] z-20 bg-white/95 backdrop-blur-md py-4 border-b border-gray-100 flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 mb-4">
                                {(managedRestaurant.menu || []).map(c => (
                                    <button
                                        key={c.category}
                                        onClick={() => jumpToCategory(c.category)}
                                        className="px-4 py-2 rounded-xl bg-gray-50 text-[10px] font-black uppercase text-gray-500 hover:bg-red-50 hover:text-red-600 transition-all whitespace-nowrap border border-gray-100"
                                    >
                                        {c.category}
                                    </button>
                                ))}
                            </div>

                            {(!managedRestaurant.dishesFetched && managedRestaurant.menu.length === 0) ? (
                                <div className="py-20 text-center space-y-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                                    <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">Chargement des plats...</p>
                                </div>
                            ) : managedRestaurant.menu.length === 0 ? (
                                <div className="py-20 text-center text-gray-400 font-bold">Aucun plat configuré.</div>
                            ) : (
                                managedRestaurant.menu.map(category => {
                                    const isExpanded = !!expandedCategories[category.category];
                                    return (
                                        <div key={category.category} id={`cat-${category.category}`} className="scroll-mt-48 bg-gray-50/30 rounded-2xl overflow-hidden border border-gray-100 shadow-sm mb-4">
                                            <div 
                                                onClick={() => toggleCategory(category.category)}
                                                className="flex justify-between items-center p-5 cursor-pointer hover:bg-gray-100/50 transition-colors active:bg-gray-100"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <ChevronIcon isOpen={isExpanded} className="w-5 h-5 text-gray-400" />
                                                    <h3 className="text-lg font-black text-gray-800 tracking-tighter uppercase italic tracking-widest">{category.category}</h3>
                                                    <span className="text-[10px] font-bold text-gray-400 bg-white px-2 py-0.5 rounded-full border border-gray-100">{category.dishes.length} plats</span>
                                                </div>
                                                <button onClick={(e) => { e.stopPropagation(); handleOpenAddModal(category.category); }} className="text-red-600 font-bold text-xs uppercase hover:bg-red-600 hover:text-white px-3 py-1.5 rounded-lg transition-all border border-red-100">+ Ajouter</button>
                                            </div>
                                            
                                            <AnimatePresence initial={false}>
                                                {isExpanded && (
                                                    <motion.div 
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        exit={{ opacity: 0 }}
                                                        transition={{ duration: 0.2 }}
                                                    >
                                                        <div className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 border-t border-gray-100/50">
                                                            {category.dishes.map(dish => (
                                                                <div key={dish.id} className="p-4 bg-white rounded-xl flex items-center justify-between group shadow-sm border border-gray-100/50 active:scale-[0.98] transition-all">
                                                                    <div className="min-w-0 flex-grow pr-4">
                                                                        <p className="font-bold text-gray-800 truncate text-sm">{dish.name}</p>
                                                                        <p className="text-xs text-red-600 font-black">{dish.price} DA</p>
                                                                    </div>
                                                                    <div className="flex gap-2">
                                                                        <button onClick={(e) => { e.stopPropagation(); handleOpenEditModal(dish, category.category); }} className="p-2.5 bg-gray-50 rounded-lg border text-gray-400 active:text-blue-600"><PencilIcon className="w-4 h-4" /></button>
                                                                        <button onClick={(e) => { e.stopPropagation(); handleDeleteDish(dish.id); }} className="p-2.5 bg-gray-50 rounded-lg border text-gray-400 active:text-red-600"><TrashIcon className="w-4 h-4" /></button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}

                    {viewMode === 'extras' && (
                        <div className="space-y-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-black text-gray-900 tracking-tighter uppercase italic">Gestion des Extras</h2>
                                <button onClick={handleOpenAddExtraModal} className="bg-green-600 text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest">+ Nouvel Extra</button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {(managedRestaurant.extras || []).map(extra => (
                                    <div key={extra.id} className="p-4 bg-gray-50 rounded-xl flex justify-between items-center">
                                        <div className="flex flex-col">
                                            <span className="font-bold text-gray-800">{extra.name}</span>
                                            <span className="text-xs font-black text-green-600">{extra.price} DA</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => handleOpenEditExtraModal(extra)} className="text-gray-400 hover:text-blue-600"><PencilIcon className="w-4 h-4" /></button>
                                            <button onClick={() => handleDeleteGlobalExtra(extra.id)} className="text-gray-400 hover:text-red-600"><TrashIcon className="w-4 h-4" /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {viewMode === 'packs' && (
                        <div className="space-y-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-black text-gray-900 tracking-tighter uppercase italic">Gestion des Packs</h2>
                                <button onClick={handleOpenAddPackModal} className="bg-red-600 text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest">+ Nouveau Pack</button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
                                {(managedRestaurant.packs || []).map(pack => (
                                    <div key={pack.id} className="bg-gray-50 rounded-[2rem] overflow-hidden border border-gray-100 flex flex-col md:flex-row shadow-sm hover:shadow-md transition-shadow">
                                        {pack.image && (
                                            <div className="md:w-32 h-32 md:h-auto">
                                                <img src={pack.image} alt={pack.name} className="w-full h-full object-cover" />
                                            </div>
                                        )}
                                        <div className="p-6 flex-grow flex flex-col justify-between">
                                            <div>
                                                <h4 className="font-black text-gray-900 text-lg mb-1">{pack.name}</h4>
                                                <div className="flex flex-wrap gap-1 mb-4">
                                                    {pack.dishes.map((d, idx) => (
                                                        <span key={idx} className="text-[10px] font-bold bg-white px-2 py-0.5 rounded-md border border-gray-200">
                                                            {d.quantity && d.quantity > 1 ? `${d.quantity}x ` : ''}{d.name}
                                                        </span>
                                                    ))}
                                                </div>
                                                <p className="text-xs text-gray-500 mb-4 line-clamp-2">{pack.description}</p>
                                            </div>
                                            <div className="flex justify-between items-end">
                                                <span className="text-lg font-black text-red-600">{pack.packPrice} DA</span>
                                                <div className="flex gap-2">
                                                    <button onClick={() => handleOpenEditPackModal(pack)} className="p-2 bg-white rounded-xl border text-gray-400 hover:text-blue-600 transition-colors"><PencilIcon className="w-4 h-4" /></button>
                                                    <button onClick={() => handleDeletePack(pack.id)} className="p-2 bg-white rounded-xl border text-gray-400 hover:text-red-600 transition-colors"><TrashIcon className="w-4 h-4" /></button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
                {/* MODAL GATES: Modals are only allowed in the DOM if their tab is active */}
                <AnimatePresence>
                    {viewMode === 'dishes' && isFormModalOpen && (
                        <DishFormModal 
                            isOpen={isFormModalOpen} 
                            onClose={handleCloseFormModal} 
                            onSave={handleSaveDish} 
                            dishToEdit={dishToEdit} 
                            initialCategory={selectedCategory} 
                            isSaving={isSaving} 
                            restaurantId={managedRestaurant.id} 
                        />
                    )}
                    
                    {viewMode === 'extras' && isExtraModalOpen && (
                        <ExtraFormModal 
                            isOpen={isExtraModalOpen} 
                            onClose={handleCloseExtraModal} 
                            onSave={handleSaveExtra} 
                            extraToEdit={extraToEdit} 
                            isSaving={isExtrasSaving} 
                            restaurantId={managedRestaurant.id} 
                        />
                    )}
                    
                    {viewMode === 'packs' && isPackModalOpen && (
                        <PackFormModal 
                            isOpen={isPackModalOpen} 
                            onClose={handleClosePackModal} 
                            onSave={handleSavePack} 
                            packToEdit={packToEdit} 
                            restaurantDishes={(managedRestaurant.menu || []).flatMap(c => c.dishes || [])} 
                            restaurantExtras={managedRestaurant.extras || []} 
                            isSaving={isPacksSaving} 
                            restaurantId={managedRestaurant.id} 
                        />
                    )}
                </AnimatePresence>

            {/* Floating Action Button (FAB) Hardened Logic */}
            {!isPageJustLoaded && (
                <div className="fixed bottom-24 right-8 z-[110]">
                    <button 
                        onClick={() => { 
                            if (viewMode === 'dishes') handleOpenAddModal(selectedCategory || 'Burgers'); 
                            else if (viewMode === 'extras') handleOpenAddExtraModal(); 
                            else if (viewMode === 'packs') handleOpenAddPackModal(); 
                        }} 
                        className={`w-16 h-16 text-white rounded-full flex items-center justify-center shadow-2xl border-4 border-white active:scale-95 transition-all ${viewMode === 'dishes' ? 'bg-red-600' : viewMode === 'extras' ? 'bg-green-600' : 'bg-blue-600'}`}
                    >
                        <PlusIcon className="w-8 h-8" />
                    </button>
                </div>
            )}

        </div>
    );
};

export default AdminDishesScreen;